import { AccessTokenClaimsSchema, type AccessTokenClaims } from '@aysar/contracts';

/**
 * Decodes the payload segment of a JWT.
 *
 * `atob` yields one byte per character, so a payload containing Arabic — a role
 * name, for instance — has to be walked back through `TextDecoder` or it arrives
 * mojibake'd. The base64url alphabet also has to be translated and the padding
 * restored, neither of which `atob` does.
 */
function decodePayloadSegment(segment: string): unknown {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));

  return JSON.parse(new TextDecoder().decode(bytes));
}

function payloadOf(token: string): unknown {
  const segments = token.split('.');
  if (segments.length !== 3) return undefined;

  const payload = segments[1];
  if (payload === undefined || payload === '') return undefined;

  try {
    return decodePayloadSegment(payload);
  } catch {
    return undefined;
  }
}

/**
 * The actor's own authorization state, read from their access token.
 *
 * This is the ONLY client-side source of it: there is no `GET /me/access` for
 * oneself, and that is deliberate — P2 stamped roles, granted, denied and the
 * super-admin flag into the token precisely so the guard at the edge and the UI
 * evaluate the same facts through the same `hasPermission` (N22).
 *
 * The signature is NOT verified, and it must not be: the client cannot hold the
 * signing secret, so any check here would be theatre. Everything this returns is
 * used to decide what to RENDER. Every actual authorization decision happens on
 * the api, which verifies the signature and re-checks `tokenVersion` per
 * request — so a forged token buys a forger a menu item that 403s.
 *
 * Anything unexpected resolves to `null`, which every caller treats as "signed
 * out with no permissions" (fail-closed).
 */
export function decodeAccessClaims(token: string): AccessTokenClaims | null {
  const payload = payloadOf(token);
  if (payload === undefined) return null;

  // Not `.strict()`: the token also carries `iat`/`exp`/`iss`, which a strict
  // parse would reject outright. Unknown keys are stripped, known ones validated.
  const parsed = AccessTokenClaimsSchema.safeParse(payload);
  return parsed.success ? parsed.data : null;
}

/**
 * The token's expiry in epoch milliseconds, or `null` when it carries none.
 *
 * Read off the raw payload rather than the parsed claims, because `exp` is a
 * standard JWT claim that `AccessTokenClaimsSchema` deliberately does not model.
 */
export function claimsExpiry(token: string): number | null {
  const payload = payloadOf(token);
  if (payload === null || typeof payload !== 'object') return null;

  const exp = (payload as Record<string, unknown>)['exp'];
  return typeof exp === 'number' && Number.isFinite(exp) ? exp * 1000 : null;
}
